import { mt } from '@passionware/monads';
import React from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormRootElement
} from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { FormSaveButton } from 'v5/patterns/forms/dialogs/FormSaveButton';

interface CreateProfileFormData {
  firstName: string;
  lastName: string;
}

interface TranslatedFormMessageProps {
  error?: FieldError;
}

interface CreateProfilePanelProps {
  onSubmit: (data: CreateProfileFormData) => Promise<void>;
}

// Custom FormMessage that translates error keys
const TranslatedFormMessage: React.FC<TranslatedFormMessageProps> = ({
  error
}) => {
  const { t } = useTranslation('v1-features');

  if (!error) return null;

  const message = typeof error.message === 'string' ? error.message : '';

  return (
    <p className="text-base font-medium text-fg-danger">
      {t(message, { defaultValue: message })}
    </p>
  );
};

const CreateProfilePanel: React.FC<CreateProfilePanelProps> = ({
  onSubmit
}) => {
  const { t } = useTranslation('v1-features');

  const form = useForm<CreateProfileFormData>({
    defaultValues: {
      firstName: '',
      lastName: ''
    },
    mode: 'onBlur'
  });

  return (
    <div className="tailwind-root contents">
      <Form {...form}>
        <FormRootElement
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 w-[350px]"
        >
          <FormField
            control={form.control}
            name="firstName"
            rules={{
              required: 'CreateProfilePanel.errors.firstNameRequired'
            }}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="font-semibold text-fg-default">
                  {t('CreateProfilePanel.firstNameLabel')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    size="md"
                    placeholder={t('CreateProfilePanel.firstNamePlaceholder')}
                    {...field}
                  />
                </FormControl>
                <TranslatedFormMessage error={fieldState.error} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            rules={{
              required: 'CreateProfilePanel.errors.lastNameRequired'
            }}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="font-semibold text-fg-default">
                  {t('CreateProfilePanel.lastNameLabel')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    size="md"
                    placeholder={t('CreateProfilePanel.lastNamePlaceholder')}
                    {...field}
                  />
                </FormControl>
                <TranslatedFormMessage error={fieldState.error} />
              </FormItem>
            )}
          />

          <div className="flex justify-end pt-2">
            <FormSaveButton
              mutation={
                form.formState.isSubmitting ? mt.ofPending({}) : mt.ofIdle()
              }
              saveText={t('CreateProfilePanel.continueButton')}
              variant="primaryAlt"
              showHotKey={false}
            />
          </div>
        </FormRootElement>
      </Form>
    </div>
  );
};

export default CreateProfilePanel;
